import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import {
  alpha,
  Badge,
  Box,
  BoxProps,
  Container,
  IconButton,
  Link,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import { useMeasure } from '@reactuses/core'
import gsap from 'gsap'
import Flip from 'gsap/dist/Flip'
import { default as NextLink } from 'next/link'
import { useRouter } from 'next/router'

import LanguageSwitcher from '@/components/base/language-switcher'
import LogoHappa from '@/components/base/logo-happa'
import LogoSais from '@/components/base/logo-sais'
import MenuSwitcher from '@/components/base/menu-switcher'
import { useLenisStore } from '@/contexts/lenis'
import { useAppStore } from '@/contexts/store'
import { isHappa, isSais } from '@/hooks'
import { useHydrated } from '@/hooks/use-hydrated'
import { useLocaleStore } from '@/store/locale'
import { useWishlistStore } from '@/store/wishlist'
import { isActive } from '@/utils/path'

import { useAuth } from '@/api/auth/use-auth'

const SCROLL_THRESHOLD = 50

const Header = (props: BoxProps) => {
  const { isAuthenticated } = useAuth()
  const hydrated = useHydrated()
  const nav = useRef<HTMLDivElement>(null)
  const lang = useRef<HTMLDivElement>(null)
  const [navRect] = useMeasure(nav)
  const [langRect] = useMeasure(lang)
  const { menu, strings } = useAppStore()
  const [isMinimized, setIsMinimized] = useState(false)
  const selector = useRef<HTMLDivElement>(null!)
  const menuItems = useRef<HTMLElement[]>([])
  const root = useRef<HTMLDivElement>(null!)
  const router = useRouter()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const wishlistItems = useWishlistStore((state) => state.items)
  const [mounted, setMounted] = useState(false)

  const offset = useMemo(() => {
    const multiplier = strings?.header?.user_link?.url ? 3 : 2
    return navRect.width - langRect.width - 48 * multiplier - 8 * multiplier
  }, [navRect, langRect, strings?.header?.user_link?.url])

  // Handle hydration mismatch for wishlist
  useEffect(() => {
    setMounted(true)
  }, [])

  // Trova l'indice dell'elemento attivo basato sul path corrente
  const getActiveMenuIndex = useCallback(() => {
    const index = menu?.main_menu_01?.menu?.findIndex(({ url }) => isActive(url, router.asPath))
    return index !== undefined && index >= 0 ? index : 0
  }, [menu, router.asPath])

  const [activeIndex, setActiveIndex] = useState(getActiveMenuIndex)
  const defaultIndex = getActiveMenuIndex()

  useIsomorphicLayoutEffect(() => {
    gsap.killTweensOf(root.current)
    gsap
      .timeline()
      .set(root.current, { pointerEvents: 'none' }, 0)
      .set(root.current, { pointerEvents: 'auto' }, 0.3)
  }, [router])

  // Aggiorna l'indice di default quando cambia la route
  useEffect(() => {
    setActiveIndex(getActiveMenuIndex())
  }, [router.asPath, menu, getActiveMenuIndex])

  useEffect(() => {
    if (activeIndex < 0) return
    const state = Flip.getState(selector.current, { simple: true })
    const activeItem = menuItems.current[activeIndex]
    if (!activeItem) return
    activeItem.append(selector.current)
    Flip.from(state, { duration: 0.5, ease: 'power2.out' })
  }, [activeIndex])

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => state.scroll,
      (scroll) => setIsMinimized(scroll > SCROLL_THRESHOLD)
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <Box
      component="header"
      ref={root}
      sx={{
        position: 'fixed',
        zIndex: 100,
        width: '100%',
        borderBottom: '0.1rem solid',
        borderColor: (theme) => theme.palette.grey[100],
        background: (theme) => theme.palette.text.primaryContrastText
      }}
      {...props}
    >
      <Container
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          pt: theme.spacing(1),
          pb: theme.spacing(1)
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          {/**
           * nav
           */}
          <Box
            component="nav"
            ref={nav}
            sx={{
              position: 'relative',
              display: { xs: 'none', lg: 'flex' }
            }}
          >
            <Box
              sx={(theme) => ({
                p: isHappa ? 0 : '0.4rem',
                display: 'flex',
                backgroundColor: (theme) =>
                  isHappa ? 'transparent' : theme.palette.text.primaryContrastText,
                borderRadius: isHappa ? 0 : theme.shape.borderRadius360,
                boxShadow: isHappa ? 'none' : theme.shadows[2]
              })}
            >
              {menu?.main_menu_01?.menu?.map(({ title, url, acf: { icon } }, index) => (
                <Link
                  href={url}
                  scroll={false}
                  component={NextLink}
                  key={`menu-item-${index}`}
                  ref={(el: any) => (menuItems.current[index] = el!)}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(defaultIndex)}
                  sx={(theme) => ({
                    position: 'relative',
                    zIndex: 1,
                    fontFamily: theme.typography.menu,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    padding: '1.2rem 1.6rem',
                    borderRadius: isHappa ? theme.shape.borderRadius1 : theme.shape.borderRadius360,
                    color: isActive(url, router.asPath)
                      ? theme.palette.text.primaryContrastText
                      : theme.palette.primary.main,
                    background: isActive(url, router.asPath)
                      ? theme.palette.primary.main
                      : 'transparent',
                    transition: `all 0.3s ${theme.customEasings.outCubic}`
                  })}
                >
                  <SvgIcon sx={{ position: 'relative', zIndex: 2 }}>
                    <use href={`#ico-${icon}`} />
                  </SvgIcon>
                  <Typography
                    sx={{ position: 'relative', zIndex: 2 }}
                    variant="menu"
                    component="span"
                    fontWeight={500}
                  >
                    {title}
                  </Typography>
                  {index === 0 ? (
                    <Box
                      ref={selector}
                      component="div"
                      sx={{
                        position: 'absolute',
                        zIndex: 1,
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          width: '100%',
                          height: '100%',
                          borderRadius: (theme) =>
                            isHappa ? theme.shape.borderRadius1 : theme.shape.borderRadius360,
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06)
                        }}
                      />
                    </Box>
                  ) : null}
                </Link>
              ))}
            </Box>
          </Box>

          {/**
           * lang
           */}
          <Box
            ref={lang}
            sx={{
              order: { xs: 0, lg: 1 },
              marginLeft: { lg: `${offset}px` }
            }}
          >
            <LanguageSwitcher />
          </Box>

          {/**
           * logo
           */}
          <Box
            sx={(theme) => ({
              height: isMinimized
                ? theme.customSizes.headerLogoHeightMinimized
                : theme.customSizes.headerLogoHeight,

              width: 'auto',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              opacity: { lg: offset < 0 ? 0 : 1 },
              transition: `height 0.3s ${theme.customEasings.outCubic}`
            })}
          >
            {isSais && currentLocale !== undefined ? (
              <LogoSais
                href={currentLocale === 'it' ? '/' : `/${currentLocale}`}
                sx={{
                  width: { xs: '5rem', sm: isMinimized ? '5.4rem' : '10rem' },
                  transition: (theme: any) => `width 0.3s ${theme.customEasings.outCubic}`,
                  display: 'flex',
                  alignItems: 'center'
                }}
              />
            ) : null}
            {isHappa && currentLocale !== undefined ? (
              <LogoHappa
                href={currentLocale === 'it' ? '/' : `/${currentLocale}`}
                sx={{
                  width: { xs: '8rem', sm: '17rem' },
                  transition: (theme: any) => `width 0.3s ${theme.customEasings.outCubic}`,
                  display: 'flex',
                  alignItems: 'center'
                }}
              />
            ) : null}
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              width: { xs: langRect.width || 'auto', lg: 'auto' },
              marginLeft: { lg: 1 },
              gap: 1,
              order: { xs: 0, lg: 1 }
            }}
          >
            {/**
             * wishlist
             */}
            {mounted ? (
              <IconButton
                size="medium"
                component={NextLink}
                href="/advertisements/wishlist"
                color="secondary"
                scroll={false}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  backgroundColor: (theme) =>
                    isHappa ? 'transparent' : alpha(theme.palette.primary.main, 0.06)
                }}
              >
                <Badge
                  badgeContent={wishlistItems.length}
                  color="primary"
                  invisible={wishlistItems.length === 0}
                >
                  <SvgIcon fontSize="medium">
                    <use href={`#ico-heart${wishlistItems.length > 0 ? '-filled' : ''}`} />
                  </SvgIcon>
                </Badge>
              </IconButton>
            ) : null}

            {/**
             * user
             */}
            {strings?.header?.user_link?.url ? (
              <IconButton
                size="medium"
                component={NextLink}
                href={
                  hydrated && isAuthenticated ? '/auth/dashboard' : strings.header.user_link.url
                }
                color="secondary"
                scroll={false}
                sx={(theme) => ({
                  backgroundColor: isHappa ? 'transparent' : alpha(theme.palette.primary.main, 0.06)
                })}
              >
                <SvgIcon fontSize="medium">
                  <use href="#ico-user" />
                </SvgIcon>
              </IconButton>
            ) : null}

            {/**
             * menu
             */}
            <MenuSwitcher />
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

export default Header
